import React, {
  useMemo,
  memo,
  useContext,
  useRef,
  useCallback,
  useEffect,
  type JSX,
} from 'react';
import { standardkomponent } from '../_Common/Components/BusinessCollectionComponents/businessCollectionComponents';
import { findCollectionResult } from '../../../Model/avenyModelUtils';
import {
  IMasterConfig,
  IDetailConfig,
  TSelectedDetail,
} from '../../../Types/MasterDetailTypes';
import { IClientBusinessModel } from '../../../Types/ClientModelTypes';
import { IRutinInfo } from '../../../Types/RutinInfoType';
import { ModelContext } from '../../../Model/ModelContext/ModelContext';
import StandardRutinMaster, { ICurrentSorting } from './StandardRutinMaster';
import { useLogStartedRoutine } from './Mutations/LogStartedRoutineMutation/LogStartedRoutineHook';
import { MdModalRoute } from '../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import ErrorBoundary from '../../../Components/ErrorBoundary/ErrorBoundary';
import { InternalError } from '../../../Components/InternalError/InternalError';
import { VisningsTypContextProvider } from '../_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';
import { DetaljurvalContextProvider } from '../_Common/Components/BusinessCollectionComponents/DetaljUrvalContext/DetaljUrvalContext';
import { useState } from 'react';
import { MdDetailRight } from '../../../Components/MasterDetail/Components/MdDetailRight/MdDetailRight';
import { getEmptyUrval } from '../../../Components/Urval/Utils/urvalUtils';
import { IUIUrval } from '../../../Types/UrvalTypes';
import defaultColumns from '../_Common/Utils/defaultColumns';
import {
  findRoutineDetaljVyCollectionName,
  rutiner,
} from '../_Common/Const/Rutiner';
import { ShowDetailsRight } from '../_Common/Const/Delsystem';
import { IBusinessDataFormContext } from '../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  defaultEditMenuSaveButton,
  IEditMenuActionParams,
} from '../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import Dialog from '../../../BaseComponents/Dialog/EDDialog';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import {
  useBusinessCollectionMenuItemsType,
  useItemFunctionMenuItemsType,
} from '../../../Utils/ObjektVyUtils';
import useShowDetailsRightDisabledOnCurrentScreenSize from './Hooks/useShowDetailsRightDisabledOnCurrentScreenSize';
import useForceShowDetailsRightDisabledOnScreenSize from './Hooks/useForceShowDetailsRightDisabledOnScreenSize';
import { AvenyContext } from '../../AvenyContext';
import { DocumentNode, useApolloClient } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import { useNavigate, useLocation } from 'react-router';
import {
  ActiveRoutineContext,
  ActiveRoutineContextProvider,
} from '../../Routing/ActiveRoutine/ActiveRoutineContext';
import {
  useIsTouchDevice,
  useWidth,
} from '../../../Components/BreakpointContext/BreakpointContextUtils';
import { useBusinessConfig } from '../../../Components/BusinessConfigContext/BusinessConfigContext';
import StandardRutinMasterDetailPanel from '../_Common/Components/StandardRutinMasterDetailPanel/StandardRutinMasterDetailPanel';
import { useIsExtraSmallScreen } from '../../../Hooks/useFullscreenView';
import EDBox from '../../../BaseComponents/EDBox/EDBox';
import { filterData } from '../_Common/Components/ListFilteringComponent/Utils/listFilterComponentUtils';
import {
  ISortInfo,
  ISortProperty,
  sortRows,
  standardRutinCompare,
  standardRutinGetSortValue,
} from '../../../BaseComponents/Table/Components/SortableTableUtils';
import { getDefaultSortProperties } from './StandardRutinUtils';
import { ICollectionDataType } from '../_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { useActiveDBCollectionQuery } from '../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';
import { addAllDataDependencies } from '../_Common/Const/BusinessConfigs';
import { NodeUrvalQuery_node_TEDSelection_order } from '../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/_QueryTypes/NodeUrvalQuery';
import { getColumnWithCaption } from '../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialogUtils';
import {
  definedSelectedId,
  NewId,
} from '../../../Components/MasterDetail/MasterDetailUtils';
import { StandardRutinCollectionContextProvider } from './StandardRutinCollectionContext';
import BusinessCollectionMenuSmall from '../_Common/Components/BusinessCollectionComponents/BusinessCollectionMenu/BusinessCollectionMenuSmall';
import FloatingBusinessCollectionMenu from '../_Common/Components/BusinessCollectionComponents/BusinessCollectionMenu/FloatingBusinessCollectionMenu';
import ServerError from '../../../Components/ServerError/ServerError';
import { checkTrailingSlash } from '../../../Utils/utils';
import { StandardRutinCollectionMenuDrawer } from './Components/StandardRutinCollectionMenuDrawer';
import { StandardRutinContextProvider } from './StandardRutinContextProvider';
import { sortBy } from 'ramda';
import { useIsFloatingMenuVisible } from '../../../Components/FloatingBottomArea/FloatingMenu';

const createMasterConfig = (
  collection: IClientBusinessModel,
  rutinKod: string,
  // Temp until implementation is decided upon
  showCheckboxes?: boolean,
  startWithEmptyUrval?: boolean,
  useItemFunctionMenuItems?: useItemFunctionMenuItemsType,
  useBusinessCollectionMenuItems?: useBusinessCollectionMenuItemsType
): IMasterConfig => ({
  comp: StandardRutinMaster(
    collection,
    rutinKod,
    showCheckboxes,
    startWithEmptyUrval,
    useItemFunctionMenuItems,
    useBusinessCollectionMenuItems
  ),
  caption: 'Innehåll',
});

/** Tom lista av rader */
const noRows: ICollectionDataType[] = [];

const createDetailConfig = (
  collection: IClientBusinessModel,
  rutinKod: string
): IDetailConfig => {
  const detaljVyCollectionName =
    findRoutineDetaljVyCollectionName(rutinKod) ?? collection.CollectionName;

  return {
    comp: standardkomponent(
      detaljVyCollectionName,
      false,
      rutinKod
    ) as IDetailConfig['comp'],
    caption: collection.RootType.SOLabel,
  };
};

interface IProps {
  businessCollectionName: string;
  rutinInfo: IRutinInfo;
  // Temp until implementation is decided upon
  showCheckboxes?: boolean;
  startWithEmptyUrval?: boolean;
  useItemFunctionMenuItems?: useItemFunctionMenuItemsType;
  useBusinessCollectionMenuItems?: useBusinessCollectionMenuItemsType;
  standardRutinWrapper?: React.ComponentType<
    React.PropsWithChildren<IStandardRutinWrapperProps>
  >;
}

export interface IStandardRutinWrapperProps {
  urval?: IUIUrval | null;
  setUrval?: React.Dispatch<React.SetStateAction<IUIUrval | null>>;
}
interface IBaseProps {
  collection: IClientBusinessModel;
  rutinInfo: IRutinInfo;
  // Temp until implementation is decided upon
  showCheckboxes?: boolean;
  startWithEmptyUrval?: boolean;
  useItemFunctionMenuItems?: useItemFunctionMenuItemsType;
  useBusinessCollectionMenuItems?: useBusinessCollectionMenuItemsType;
  standardRutinWrapper?: React.ComponentType<
    React.PropsWithChildren<IStandardRutinWrapperProps>
  >;
  urval: IUIUrval | null;
  setUrval: React.Dispatch<React.SetStateAction<IUIUrval | null>>;
  /** Initiering som ska göra innan vi hämtar data till innehållslistan är klar */
  initialized?: boolean;
}

export type FormStateRefType =
  React.MutableRefObject<IBusinessDataFormContext<any> | null>;

export const defaultShowDetailsRight = (
  rutinKod: string,
  showDetailsRightDisabledOnCurrentScreenSize: boolean
) => {
  const rutin = rutiner[rutinKod];
  if (
    rutin &&
    rutin.defaultShowDetailsRight &&
    !showDetailsRightDisabledOnCurrentScreenSize
  ) {
    return rutin.defaultShowDetailsRight === ShowDetailsRight.DefaultTrue;
  } else {
    return false;
  }
};

export type SelectDetailType = (
  id: TSelectedDetail,
  /** Välj detail utan att kontrollera om det finns ändringar.
   *  Om man sparar och väljer ny detail i samma steg innan state hinner uppdateras och vet att det inte kan finnas ändringar */
  force?: boolean,
  onNavigate?: () => void
) => void;

export type SelectNewDetailType = (
  customDetailData?: unknown,
  urlParams?: Record<string, string>
) => void;

/** Wrapper that does nothing */
const EmptyWrapper: React.FunctionComponent<
  React.PropsWithChildren<IStandardRutinWrapperProps>
> = props => {
  return <>{props.children}</>;
};

export const StandardRutinBase: React.FunctionComponent<
  React.PropsWithChildren<IBaseProps>
> = memo((props: IBaseProps) => {
  const rutin = rutiner[props.rutinInfo.rutinKod];
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const avenyContext = useContext(AvenyContext);
  const setOpenChoiceDialog = avenyContext.setOpenChoiceDialog;
  const isExtraSmall = useIsExtraSmallScreen();
  const modelContext = React.useContext(ModelContext);
  const formStateRef = useRef<IBusinessDataFormContext<any> | null>(null);
  const selectNextDetail = useRef<(() => void) | null>(null);
  const [saveFormLoading, setSaveFormLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mobileLayout = useIsTouchDevice();
  const isFloatingMenuVisible = useIsFloatingMenuVisible();

  const disabledReason = formStateRef.current
    ? defaultEditMenuSaveButton.isDisabled?.({
        businessDataFormContext: formStateRef.current,
        editMenuOnCancel: undefined,
      })
    : undefined;

  useLogStartedRoutine(props.rutinInfo.rutinID);

  const [searchText, setSearchText] = useState('');

  const selectedLayout = activeRoutineContext?.selectedLayout;
  const currentWidth = useWidth();
  const [columns, setColumns] = useState(
    defaultColumns(
      props.collection.CollectionName,
      props.rutinInfo.rutinKod,
      selectedLayout?.columns,
      currentWidth
    )
  );
  useEffect(() => {
    if (selectedLayout && !activeRoutineContext?.columnsInitialized) {
      setColumns(
        defaultColumns(
          props.collection.CollectionName,
          props.rutinInfo.rutinKod,
          selectedLayout?.columns,
          currentWidth
        )
      );
      activeRoutineContext.setColumnsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLayout]);

  const businessConfig = useBusinessConfig(props.collection.CollectionName);
  const [refetchButtonQueries, setRefetchButtonQueries] = useState<
    Array<DocumentNode | string>
  >(businessConfig?.refetchButtonQueries ?? []);

  const refetchEvictTypes = rutin.urvalCacheInvalidation;

  const addRefetchButtonQuery = useCallback(
    (
      newRefetchButtonQueries:
        | Array<DocumentNode | string>
        | DocumentNode
        | string
    ) => {
      setRefetchButtonQueries(prevRefetchButtonQueries => {
        let newRefetchButtonQueriesArray: Array<DocumentNode | string> = [];
        if (Array.isArray(newRefetchButtonQueries)) {
          newRefetchButtonQueriesArray = newRefetchButtonQueries;
        } else {
          newRefetchButtonQueriesArray = [newRefetchButtonQueries];
        }
        const newValues: string[] = newRefetchButtonQueriesArray
          .map(x =>
            typeof x === 'string' ? x : getOperationName(x) ?? '_null_'
          )
          .filter(x => x !== '_null_' && !prevRefetchButtonQueries.includes(x));
        if (newValues.length === 0) {
          return prevRefetchButtonQueries;
        }
        return prevRefetchButtonQueries.concat(newValues);
      });
    },
    [setRefetchButtonQueries]
  );

  const [selectedDetailId, setSelectedDetailId] =
    useState<TSelectedDetail>(null);

  const masterConfig = useMemo<IMasterConfig>(
    () =>
      createMasterConfig(
        props.collection,
        props.rutinInfo.rutinKod,
        props.showCheckboxes,
        props.startWithEmptyUrval,
        props.useItemFunctionMenuItems,
        props.useBusinessCollectionMenuItems
      ),
    [
      props.collection,
      props.rutinInfo.rutinKod,
      props.showCheckboxes,
      props.startWithEmptyUrval,
      props.useBusinessCollectionMenuItems,
      props.useItemFunctionMenuItems,
    ]
  );

  const showDetailsRightDisabledOnCurrentScreenSize =
    useShowDetailsRightDisabledOnCurrentScreenSize();

  const [showDetailsRight, setShowDetailsRight] = useState<boolean>(() => {
    return defaultShowDetailsRight(
      props.rutinInfo.rutinKod,
      showDetailsRightDisabledOnCurrentScreenSize
    );
  });

  useEffect(() => {
    if (
      activeRoutineContext?.selectedLayout &&
      !activeRoutineContext.showDetailsRightInitialized
    ) {
      setShowDetailsRight(
        Boolean(activeRoutineContext.selectedLayout.showDetailsRight)
      );
      activeRoutineContext.setShowDetailsRightInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoutineContext?.selectedLayout]);

  useForceShowDetailsRightDisabledOnScreenSize(
    props.rutinInfo.rutinKod,
    showDetailsRight,
    setShowDetailsRight
  );

  const detailConfig = useMemo<IDetailConfig>(
    () => createDetailConfig(props.collection, props.rutinInfo.rutinKod),
    [props.collection, props.rutinInfo.rutinKod]
  );

  const selectDetail: SelectDetailType = useCallback(
    (id, force, onNavigate) => {
      if (!force && formStateRef.current) {
        if (!formStateRef.current.formState.formHasChanged) {
          setSelectedDetailId(id);
          onNavigate?.();
        } else {
          setOpenChoiceDialog(true, () => {
            setSelectedDetailId(id);
            onNavigate?.();
          });
        }
      } else {
        setSelectedDetailId(id);
        onNavigate?.();
      }
    },
    [setSelectedDetailId, setOpenChoiceDialog]
  );

  const selectDetailMdRoute = useCallback(
    (selectedDetail: TSelectedDetail, urlParams?: Record<string, string>) => {
      selectDetail(selectedDetail, false, () => {
        navigate(
          {
            pathname: `${checkTrailingSlash(location.pathname)}${
              definedSelectedId(selectedDetail ?? '') || ''
            }`,
            search: `?${new URLSearchParams(urlParams).toString()}`,
          },
          { state: { disableOpenChoiceDialog: true } }
        );
      });
    },
    [location.pathname, navigate, selectDetail]
  );

  // Temporärt tills rutinen anses säker för läsläge
  // if (
  //   activeDatabaseContext.availableRoutines?.[props.rutinInfo.rutinID]
  //     ?.readonly
  // ) {
  //   return <ReadonlyPlaceholderPage />;
  // }

  const customBusinessCollectionMenuItems =
    props.useBusinessCollectionMenuItems?.();

  let filterAttributes = rutiner[props.rutinInfo.rutinKod]?.filterAttributes;

  const attributeDefs = useMemo(
    () => addAllDataDependencies(props.collection.CollectionName, columns),
    [columns, props.collection.CollectionName]
  );

  const collectionQueryResult = useActiveDBCollectionQuery({
    collectionName: props.collection.CollectionName,
    queryName: `${props.rutinInfo.rutinKod ?? 'ingenRutinkod'}Main`,
    attributeDefs: attributeDefs,
    urval: props.urval,
    urvalId: activeRoutineContext?.initialUrvalId,
    routineID: props.rutinInfo.rutinID,
    networkOnlyOnFirst: true,
    skip: !props.initialized,
  });

  /**
   * Den aktuella sorteringen av raderna, uppdateras när sorteringsordningen ändras eller man läser om urvalet från backend.
   * Vid andra ändringarn av data så ska datat sorteras efter den här ordningen.
   * Ref istället för state för att undvika onödiga omrenderingar
   */
  const currentSortRef = useRef<ICurrentSorting>({});

  /**
   * Sorteringsinställningar. Raderna ska sorteras efter detta när inställningarna ändras eller urvalet laddas om.
   */
  const [sortProperties, setSortProperties] = useState<ISortProperty[]>(
    activeRoutineContext?.selectedLayout?.sortering ??
      getDefaultSortProperties(
        columns,
        props.collection.CollectionName,
        modelContext,
        props.rutinInfo.rutinKod
      )
  );

  const sortInfo = useMemo<ISortInfo<ICollectionDataType>>(() => {
    const lastSortInfo = currentSortRef.current;
    let lastSortOrder = lastSortInfo?.sortOrder;
    const keepSortOrder =
      lastSortOrder &&
      lastSortInfo.properties === sortProperties &&
      !collectionQueryResult.loading;
    const rows = collectionQueryResult.loading
      ? noRows
      : keepSortOrder
      ? sortBy(
          row => lastSortInfo.sortOrder?.get(row.id) ?? 0,
          collectionQueryResult.data || []
        )
      : sortRows<ICollectionDataType>(
          sortProperties,
          collectionQueryResult.data || [],
          standardRutinGetSortValue(props.collection.CollectionName),
          standardRutinCompare
        );
    if (!keepSortOrder) {
      lastSortInfo.sortOrder = collectionQueryResult.loading
        ? undefined
        : new Map(rows.map((row, idx) => [row.id, idx]));
      lastSortInfo.properties = sortProperties;
    }
    return {
      rows: rows,
      properties: sortProperties,
    };
  }, [
    props.collection.CollectionName,
    collectionQueryResult.loading,
    collectionQueryResult.data,
    sortProperties,
  ]);

  useEffect(() => {
    if (
      activeRoutineContext?.selectedLayout &&
      !activeRoutineContext.sortPropertiesInitialized
    ) {
      const sortProperties = activeRoutineContext.selectedLayout.sortering;

      setSortProperties(sortProperties);
      activeRoutineContext.setSortPropertiesInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoutineContext?.selectedLayout]);

  const hasUrval = Boolean(props.urval);

  const filteredSortInfo = useMemo(
    () =>
      filterData(
        props.collection.CollectionName,
        sortInfo,
        searchText,
        filterAttributes,
        hasUrval,
        collectionQueryResult.loading
      ),
    [
      sortInfo,
      searchText,
      filterAttributes,
      hasUrval,
      collectionQueryResult.loading,
      props.collection.CollectionName,
    ]
  );

  const updateSortInfo = useCallback((info: ISortInfo<ICollectionDataType>) => {
    setSortProperties(info.properties);
  }, []);

  const setSortInfoFromSelectionOrder = useCallback(
    (selectionOrder: NodeUrvalQuery_node_TEDSelection_order[]) => {
      const sortProperties = selectionOrder.reduce<ISortProperty[]>(
        (properties, order) => {
          const column = columns.find(
            column => column.attributeDef === order.namn
          );

          if (column) {
            const columnWithCaption = getColumnWithCaption(
              props.collection.CollectionName,
              modelContext,
              column
            );

            properties.push({
              caption: columnWithCaption.caption ?? '',
              orderBy: order.namn,
              order: order.desc ? 'desc' : 'asc',
            });
          }
          return properties;
        },
        []
      );
      updateSortInfo({ ...sortInfo, properties: sortProperties });
    },
    [
      modelContext,
      props.collection.CollectionName,
      columns,
      sortInfo,
      updateSortInfo,
    ]
  );

  const selectNewDetail: SelectNewDetailType = useCallback(
    (customDetailData, urlParams) =>
      showDetailsRight
        ? selectDetail({ id: NewId, customDetailData: customDetailData })
        : selectDetailMdRoute(
            {
              id: NewId,
              customDetailData: customDetailData,
            },
            urlParams
          ),
    [selectDetail, selectDetailMdRoute, showDetailsRight]
  );

  const getLabel = (
    item:
      | string
      | ((editMenuActionParams: IEditMenuActionParams) => string | JSX.Element)
      | JSX.Element
      | undefined,
    defaultLabel: string
  ) => {
    if (typeof item === 'function' && formStateRef.current) {
      return item({
        businessDataFormContext: formStateRef.current,
        editMenuOnCancel: () => {},
      });
    } else if (typeof item === 'string') {
      return item;
    } else {
      return defaultLabel;
    }
  };

  const choiceDialog = (
    <Dialog
      title={'Välj åtgärd'}
      open={avenyContext.openChoiceDialog}
      onClose={() => avenyContext.setOpenChoiceDialog(false)}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: () => {
            avenyContext.setOpenChoiceDialog(false);
          },
        },
        {
          label: 'Ångra',
          onClick: () => {
            formStateRef.current?.formStateDispatch({
              type: 'CANCEL',
            });
            avenyContext.openChoiceCallback?.();
            avenyContext.setOpenChoiceDialog(false);
          },
        },
        {
          buttonProps: formStateRef?.current?.overrideChoiceButtons?.save
            ? {
                disabled:
                  Boolean(
                    formStateRef?.current?.overrideChoiceButtons?.save?.isDisabled?.(
                      {
                        businessDataFormContext: formStateRef.current,
                        editMenuOnCancel: () => {},
                      }
                    )
                  ) || saveFormLoading,
                disabledReason:
                  formStateRef?.current?.overrideChoiceButtons?.save?.isDisabled?.(
                    {
                      businessDataFormContext: formStateRef.current,
                      editMenuOnCancel: () => {},
                    }
                  ),
              }
            : {
                disabled: Boolean(disabledReason) || saveFormLoading,
                disabledReason: disabledReason,
              },
          label: getLabel(
            formStateRef?.current?.overrideChoiceButtons?.save?.label,
            'Spara'
          ),
          onClick: formStateRef?.current?.overrideChoiceButtons?.save?.onClick
            ? () => {
                setSaveFormLoading(true);
                formStateRef?.current?.overrideChoiceButtons?.save?.onClick?.({
                  businessDataFormContext: formStateRef.current,
                  editMenuOnCancel: () => {
                    setSaveFormLoading(false);
                  },
                });
              }
            : () => {
                setSaveFormLoading(true);
                formStateRef.current?.saveForm(
                  undefined,
                  () => {
                    avenyContext.openChoiceCallback?.();
                    avenyContext.setOpenChoiceDialog(false);
                    setSaveFormLoading(false);
                  },
                  () => {
                    setSaveFormLoading(false);
                  }
                );
              },
        },
      ]}
      isErrorDialog
    >
      <EDTypography>Det finns ändringar som inte är sparade.</EDTypography>
      <EDTypography bold>Välj åtgärd för att gå vidare</EDTypography>
    </Dialog>
  );

  if (!collectionQueryResult.query.isOk()) {
    return (
      <InternalError
        id="194684b3-8314-4d40-8039-a7567edacb72"
        message={collectionQueryResult.query.error}
      />
    );
  }

  if (collectionQueryResult.serverError) {
    return <ServerError error={collectionQueryResult.serverError} />;
  }

  return (
    <VisningsTypContextProvider
      rutinID={props.rutinInfo.rutinID}
      collectionName={props.collection.CollectionName}
      showDetailsRight={showDetailsRight}
      setShowDetailsRight={setShowDetailsRight}
      isRutin={true}
    >
      <DetaljurvalContextProvider
        rutinID={props.rutinInfo.rutinID}
        collectionName={props.collection.CollectionName}
      >
        <StandardRutinContextProvider
          rutinID={props.rutinInfo.rutinID}
          collectionName={props.collection.CollectionName}
          urval={props.urval}
          setUrval={props.setUrval}
          columns={columns}
          setColumns={setColumns}
          formStateRef={formStateRef}
          setOpenChoiceDialog={avenyContext.setOpenChoiceDialog}
          refetchButtonQueries={refetchButtonQueries}
          urvalCacheInvalidation={refetchEvictTypes}
          addRefetchButtonQuery={addRefetchButtonQuery}
          selectNextDetail={selectNextDetail}
        >
          <StandardRutinCollectionContextProvider
            sortInfo={sortInfo}
            filteredSortInfo={filteredSortInfo}
            updateSortInfo={updateSortInfo}
            collectionQueryResult={collectionQueryResult}
            searchText={searchText}
            setSearchText={setSearchText}
            filterAttributes={filterAttributes}
          >
            <EDBox
              sx={{
                display: 'flex',
                overflow: 'hidden',
                flexBasis: '100%',
                flexDirection: mobileLayout ? 'column' : 'row',
              }}
            >
              {!mobileLayout && !rutin.hideRutinCollectionMenu && (
                <StandardRutinCollectionMenuDrawer
                  collection={props.collection}
                  columns={columns}
                  setColumns={setColumns}
                  urval={props.urval}
                  setUrval={props.setUrval}
                  refetch={collectionQueryResult.refetch}
                  setNewObject={selectNewDetail}
                  sortedData={sortInfo}
                  updateSortOrder={updateSortInfo}
                  baseCollection={props.collection}
                  rutinId={activeRoutineContext?.rutinInfo.rutinID}
                  customBusinessCollectionMenuItems={
                    customBusinessCollectionMenuItems
                  }
                  datasetLength={filteredSortInfo.rows.length}
                  searchText={searchText}
                  rows={filteredSortInfo.rows}
                  setSortInfoFromSelectionOrder={setSortInfoFromSelectionOrder}
                />
              )}
              <StandardRutinMasterDetailPanel smallScreen={isExtraSmall}>
                {showDetailsRight ? (
                  <>
                    <MdDetailRight
                      master={masterConfig}
                      detail={detailConfig}
                      collection={props.collection}
                      selectedDetailId={selectedDetailId}
                      selectDetail={selectDetail}
                      size={rutin.showDetailsRightSize}
                    />
                  </>
                ) : (
                  <MdModalRoute
                    master={masterConfig}
                    detail={detailConfig}
                    selectedDetail={selectedDetailId}
                    selectDetail={selectDetail}
                    controlled
                    collection={props.collection}
                  />
                )}
              </StandardRutinMasterDetailPanel>
              {mobileLayout &&
                !rutin.hideRutinCollectionMenu &&
                (isFloatingMenuVisible ? (
                  <FloatingBusinessCollectionMenu
                    collection={props.collection}
                    columns={columns}
                    setColumns={setColumns}
                    urval={props.urval}
                    setUrval={props.setUrval}
                    refetch={collectionQueryResult.refetch}
                    setNewObject={selectNewDetail}
                    sortedData={sortInfo}
                    updateSortOrder={updateSortInfo}
                    baseCollection={props.collection}
                    baseId={null}
                    customBusinessCollectionMenuItems={
                      customBusinessCollectionMenuItems
                    }
                    datasetLength={filteredSortInfo.rows.length}
                    searchText={searchText}
                    rows={filteredSortInfo.rows}
                    setSortInfoFromSelectionOrder={
                      setSortInfoFromSelectionOrder
                    }
                  />
                ) : (
                  <BusinessCollectionMenuSmall
                    collection={props.collection}
                    columns={columns}
                    setColumns={setColumns}
                    urval={props.urval}
                    setUrval={props.setUrval}
                    refetch={collectionQueryResult.refetch}
                    setNewObject={selectNewDetail}
                    sortedData={sortInfo}
                    updateSortOrder={updateSortInfo}
                    baseCollection={props.collection}
                    baseId={null}
                    customBusinessCollectionMenuItems={
                      customBusinessCollectionMenuItems
                    }
                    datasetLength={filteredSortInfo.rows.length}
                    searchText={searchText}
                    rows={filteredSortInfo.rows}
                    setSortInfoFromSelectionOrder={
                      setSortInfoFromSelectionOrder
                    }
                  />
                ))}
            </EDBox>
            {choiceDialog}
          </StandardRutinCollectionContextProvider>
        </StandardRutinContextProvider>
      </DetaljurvalContextProvider>
    </VisningsTypContextProvider>
  );
});
StandardRutinBase.displayName = 'StandardRutinBase';
/**
 * En rutin som visar ett urval
 */
export const StandardRutin: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = (props: IProps) => {
  const apolloClient = useApolloClient();
  const rutin = rutiner[props.rutinInfo.rutinKod];
  const urvalCacheInvalidation = rutin?.urvalCacheInvalidation;
  const [isInitialized, setIsInitialized] = useState(false);

  // Initiera rutinen när komponenten mountas
  const cacheRef = useRef(apolloClient.cache);
  const urvalCacheInvalidationRef = useRef(urvalCacheInvalidation);
  useEffect(() => {
    if (!isInitialized) {
      urvalCacheInvalidationRef.current?.(cacheRef.current);
      setIsInitialized(true);
    }
  }, [isInitialized]);
  const { businessCollectionName, ...otherProps } = props;
  const modelContext = React.useContext(ModelContext);
  const collection = findCollectionResult(
    modelContext.avenyCollectionMap,
    businessCollectionName
  );

  const [urval, setUrvalState] = useState<IUIUrval | null>(
    props.startWithEmptyUrval ? getEmptyUrval() : null
  );
  // Sätt urval och men evicta enligt refetchEvictTypes först
  const setUrval = useCallback(
    (newUrval: React.SetStateAction<IUIUrval | null>) => {
      urvalCacheInvalidation?.(apolloClient.cache);
      if (typeof newUrval === 'function') {
        setUrvalState(prevUrval =>
          (newUrval as (prevState: IUIUrval | null) => IUIUrval | null)(
            prevUrval
          )
        );
      } else {
        setUrvalState(newUrval);
      }
    },
    [setUrvalState, urvalCacheInvalidation, apolloClient.cache]
  );
  const StandardRutinWrapper = props.standardRutinWrapper ?? EmptyWrapper;

  if (collection.isOk()) {
    return (
      <ErrorBoundary name={props.rutinInfo.benamning}>
        <ActiveRoutineContextProvider
          rutinInfo={props.rutinInfo}
          collectionName={businessCollectionName}
        >
          <StandardRutinWrapper urval={urval} setUrval={setUrval}>
            <StandardRutinBase
              collection={collection.value}
              urval={urval}
              setUrval={setUrval}
              initialized={isInitialized}
              {...otherProps}
            />
          </StandardRutinWrapper>
        </ActiveRoutineContextProvider>
      </ErrorBoundary>
    );
  } else {
    return (
      <InternalError
        id="ba19380d-7572-4647-91fe-0f33a96b6af5"
        message={`Standardrutin kan inte startas: ${collection.error}`}
      />
    );
  }
};

export const createStandardRutin =
  (
    rutinInfo: IRutinInfo,
    businessCollectionName: string,
    useFunctionMenuItems?: useItemFunctionMenuItemsType,
    useBusinessCollectionMenuItems?: useBusinessCollectionMenuItemsType,
    standardRutinWrapper?: React.ComponentType<
      React.PropsWithChildren<IStandardRutinWrapperProps>
    >
  ): React.FunctionComponent<React.PropsWithChildren<{}>> =>
  () =>
    (
      <StandardRutin
        businessCollectionName={businessCollectionName}
        rutinInfo={rutinInfo}
        useItemFunctionMenuItems={useFunctionMenuItems}
        useBusinessCollectionMenuItems={useBusinessCollectionMenuItems}
        standardRutinWrapper={standardRutinWrapper}
      />
    );

export default StandardRutin;
